import Close from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PickerValue } from "@mui/x-date-pickers/internals";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { useBanUserMutation } from "src/api/services/banApi";
import { useUpdatePatientMutation } from "src/api/services/patientsApi";
import { isLanguage, isPatientStatus, PatientProfile } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import DatePicker from "src/components/DatePicker";
import { genders } from "src/constants/genders";
import { languages } from "src/constants/languages";
import { patientStatuses } from "src/constants/patientStatuses";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { formatPhoneNumber, formatToE164 } from "src/utils/formatPhoneNumber";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: () => void;
  patient: PatientProfile;
}

const ProfileUpdateModal = ({ open, onClose, patient }: Props) => {
  const [error, setError] = useState("");
  const [updatePatient, { isLoading }] = useUpdatePatientMutation();
  const [banUser] = useBanUserMutation();

  const [phoneNumber, setPhoneNumber] = useState(
    patient.profile.phone_number != null
      ? formatPhoneNumber(patient.profile.phone_number)
      : null,
  );
  const [dateOfBirth, setDateOfBirth] = useState(
    patient.profile.dob != null ? parseISO(patient.profile.dob) : null,
  );
  const [gender, setGender] = useState(patient.profile.gender);
  const [language, setLanguage] = useState(patient.profile.language);
  const [status, setStatus] = useState(patient.status);
  const [firstName, setFirstName] = useState(
    patient.profile.first_name != null ? patient.profile.first_name : null,
  );
  const [lastName, setLastName] = useState(
    patient.profile.last_name != null ? patient.profile.last_name : null,
  );
  const [promotionOpt, setPromotionOpt] = useState(patient.promotions_opt);
  const [reason, setReason] = useState("");
  const errorLog = useSentryLogger("error");

  const handlePhoneNumber = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPhoneNumber(formatPhoneNumber(event.target.value));
    },
    [],
  );

  const handleBirthday = useCallback((value: PickerValue) => {
    setDateOfBirth(value);
  }, []);

  const handleStatus = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newStatus = event.target.value;
    if (isPatientStatus(newStatus)) {
      setStatus(newStatus);
    }
  }, []);

  const handleLanguage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newLanguage = event.target.value;
    if (isLanguage(newLanguage)) {
      setLanguage(newLanguage);
    }
  }, []);

  const handleGender = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setGender(event.target.value);
  }, []);

  const handleFirstName = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFirstName(event.target.value);
    },
    [],
  );

  const handleLastName = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
  }, []);

  const handleReasonChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setReason(e.target.value);
    },
    [],
  );

  const handlePromotionOpt = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    const isSubscribed = value === "true";

    setPromotionOpt(isSubscribed);
  }, []);

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setError("");

      if (status === "Banned") {
        banUser({ userId: patient.profile_id, reason })
          .unwrap()
          .catch((err) => {
            setError("Failed to ban user.");
            errorLog("Failed to ban user:", err);
          });
      }

      updatePatient({
        profile_id: patient.profile_id,
        id: patient.id,
        promotions_opt: promotionOpt,
        status,
        profile: {
          language,
          phone_number:
            phoneNumber != null ? formatToE164(phoneNumber) : undefined,
          gender,
          dob:
            dateOfBirth != null
              ? format(new Date(dateOfBirth), "yyyy-MM-dd")
              : undefined,
          first_name: firstName != null ? firstName : undefined,
          last_name: lastName != null ? lastName : undefined,
        },
      })
        .unwrap()
        .catch((err) => setError(err.message))
        .then(() => {
          onClose();
        });
    },
    [
      banUser,
      dateOfBirth,
      errorLog,
      firstName,
      gender,
      language,
      lastName,
      onClose,
      patient.id,
      patient.profile_id,
      phoneNumber,
      promotionOpt,
      reason,
      status,
      updatePatient,
    ],
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={4} component="form" onSubmit={onSubmit}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Profile updates:
          </Typography>
          <IconButton onClick={onClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={2}>
          <FormControl>
            <TextField
              required
              label="First name"
              value={firstName}
              onChange={handleFirstName}
            />
          </FormControl>

          <FormControl>
            <TextField
              required
              label="Last name"
              value={lastName}
              onChange={handleLastName}
            />
          </FormControl>

          <FormControl>
            <TextField
              required
              label="Phone number"
              value={phoneNumber}
              onChange={handlePhoneNumber}
            />
          </FormControl>

          <FormControl>
            <DatePicker value={dateOfBirth} onChange={handleBirthday} />
          </FormControl>

          <FormControl>
            <TextField
              required
              select
              label="Gender"
              value={gender}
              onChange={handleGender}
            >
              {genders.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>

          <FormControl>
            <TextField
              select
              required
              label="Preferred language"
              value={language}
              onChange={handleLanguage}
            >
              {languages.map((lang) => (
                <MenuItem key={lang} value={lang}>
                  {lang.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>

          <FormControl>
            <TextField
              select
              required
              label="Status"
              value={status}
              onChange={handleStatus}
              disabled={patient.status === "Banned"}
            >
              {patientStatuses.map((st) => (
                <MenuItem key={st} value={st}>
                  {st}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          {status === "Banned" ? (
            <FormControl>
              <TextField
                label={"Reason for ban"}
                fullWidth
                required
                value={reason}
                onChange={handleReasonChange}
                disabled={patient.status === "Banned"}
              />
            </FormControl>
          ) : null}
          <FormControl>
            <TextField
              select
              required
              label="Promotion Opt"
              value={promotionOpt}
              onChange={handlePromotionOpt}
            >
              {["true", "false"].map((st, idx) => (
                <MenuItem key={idx} value={st}>
                  {st}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        </Stack>

        <Stack gap={1}>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}
          <LoadingButton loading={isLoading} type="submit">
            Update
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default ProfileUpdateModal;
